import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { GET_TICKET_MANAGERS } from "../TicketingAPI.js";
import { getDataSourceIcon } from "../TicketingUtils.js";
import CreateTicketSelect, {
  type CreateTicketSelectOption,
} from "../CreateTicketSelect/CreateTicketSelect.js";
import { SkeletonLoader } from "@SeemplicityDev/shared";
import type { TicketManager } from "../useTicketManagers/useTicketManagers";

// Raw provider as returned by GET_TICKET_MANAGERS -> ticket_managers[].providers[].
interface TicketManagerProviderResponse {
  id?: string;
  display?: string;
}

// Raw ticket manager entry as returned by GET_TICKET_MANAGERS -> ticket_managers[].
interface TicketManagerResponse {
  type?: string;
  endpoint_type?: string;
  implements_column_mapping?: boolean;
  must_have_mapping?: boolean;
  friendly_name?: string;
  providers?: TicketManagerProviderResponse[];
}

interface GetTicketManagersData {
  ticket_managers?: TicketManagerResponse[];
}

export interface TicketManagersProps {
  context: string;
  onChange: (ticketManager?: TicketManager) => void;
  label?: string | null;
  partnerExternalId?: string;
}

const TicketManagers: React.FC<TicketManagersProps> = ({
  context,
  onChange,
  label = null,
  partnerExternalId,
}) => {
  const [ticketManagers, setTicketManagers] = useState<TicketManager[] | null>(
    null
  );
  const [selectedTicketManager, setSelectedTicketManager] = useState<
    string | null
  >(null);

  const {
    data: ticketProvidersData,
    loading,
    // error,
  } = useQuery<GetTicketManagersData>(GET_TICKET_MANAGERS, {
    variables: {
      providers_query_context: context,
      partner_external_id: partnerExternalId || undefined,
    },
  });

  useEffect(() => {
    if (!!ticketProvidersData) {
      const ticketManagersArray = ticketProvidersData?.ticket_managers?.map(
        (ticketManager) => {
          // Original JS passed `ticketManager?.type` (possibly undefined)
          // directly; cast preserves that exact runtime call. getDataSourceIcon
          // handles missing keys by returning undefined for the unknown icon.
          const leftImg = getDataSourceIcon(ticketManager?.type as string, "L");

          return {
            name: ticketManager?.type,
            label: "",
            value: ticketManager?.type,
            friendlyName: ticketManager?.friendly_name,
            mustHaveMapping: ticketManager?.must_have_mapping,
            mustHaveColumnsMapping: ticketManager?.implements_column_mapping,
            type: ticketManager?.endpoint_type,
            leftImg,
            providers: ticketManager?.providers?.map((ticketProvider) => ({
              name: ticketProvider?.display,
              id: ticketProvider?.id,
            })),
          };
        }
      );

      setTicketManagers(ticketManagersArray ?? null);
    }
  }, [ticketProvidersData, getDataSourceIcon]);

  const ticketManagerOptions = useMemo<
    CreateTicketSelectOption[] | undefined
  >(() => {
    if (!!ticketManagers?.length) {
      return ticketManagers
        ?.filter((ticketManager) => ticketManager?.providers?.length)
        ?.map((ticketManager) => {
          return {
            value: ticketManager?.name,
            leftImg: getDataSourceIcon(ticketManager?.name as string, "S"),
            label: ticketManager?.friendlyName,
          };
        });
    }
  }, [ticketManagers, getDataSourceIcon]);

  return !loading && (ticketManagerOptions?.length ?? 0) > 0 ? (
    <CreateTicketSelect
      selectedTicketManager={selectedTicketManager}
      ticketManagerOptions={ticketManagerOptions}
      onChange={(selectedTicketManager: string) => {
        setSelectedTicketManager(selectedTicketManager);
        const selectedTicketManagerProps = ticketManagers?.find(
          (ticketManager) => ticketManager?.value === selectedTicketManager
        );
        onChange(selectedTicketManagerProps);
      }}
      label={label}
      setDefaultSingleOption
      disabled={ticketManagerOptions?.length === 1}
    />
  ) : (
    <SkeletonLoader width={"100%"} height={"26px"} />
  );
};

export default TicketManagers;
